import React, { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import Database from "@/pages/Database";
import Discover from "@/pages/Discover";
import Kpi from "@/pages/Kpi";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import ProspectDrawer from "@/components/ProspectDrawer";
import ProspectForm from "@/components/ProspectForm";
import AuthCallback from "@/components/AuthCallback";
import { AuthProvider, useAuth } from "@/context/AuthContext";

function MainApp() {
  const [drawer, setDrawer] = useState({ open: false, prospect: null });
  const [form, setForm] = useState({ open: false, prospect: null });
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const openProspect = useCallback((p) => setDrawer({ open: true, prospect: p }), []);
  const openNew = useCallback(() => setForm({ open: true, prospect: null }), []);
  const openEdit = useCallback((p) => { setDrawer((d) => ({ ...d, open: false })); setForm({ open: true, prospect: p }); }, []);
  const setDrawerOpen = useCallback((o) => setDrawer((d) => ({ ...d, open: o })), []);
  const setFormOpen = useCallback((o) => setForm((f) => ({ ...f, open: o })), []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Sidebar />
      <main className="ml-60 min-h-screen">
        <Routes>
          <Route path="/" element={<Dashboard onOpenProspect={openProspect} key={`d${refreshKey}`} />} />
          <Route path="/pipeline" element={<Pipeline onOpenProspect={openProspect} refreshKey={refreshKey} />} />
          <Route path="/database" element={<Database onOpenProspect={openProspect} onNew={openNew} refreshKey={refreshKey} />} />
          <Route path="/discover" element={<Discover onSaved={refresh} />} />
          <Route path="/kpi" element={<Kpi key={`k${refreshKey}`} />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <ProspectDrawer
        prospect={drawer.prospect}
        open={drawer.open}
        onOpenChange={setDrawerOpen}
        onChanged={refresh}
        onEdit={openEdit}
        onDeleted={refresh}
      />
      <ProspectForm
        open={form.open}
        prospect={form.prospect}
        onOpenChange={setFormOpen}
        onSaved={refresh}
      />
    </div>
  );
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <MainApp />;
}

function Root() {
  const location = useLocation();
  const { authRequired, loading } = useAuth();
  // Detect OAuth callback synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={!loading && !authRequired ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/*" element={<Protected />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Root />
        </AuthProvider>
        <Toaster position="top-right" theme="dark" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
